import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Briefcase,
  Calendar,
  MapPin,
  ChevronDown,
  ChevronUp,
  Building,
  Code,
  TrendingUp,
  Sparkles,
} from "lucide-react";

const experiences = [
  {
    id: 1,
    company: "Acuity Groups LLP",
    location: "Bengaluru, Karnataka",
    position: "Digital Marketing & Front-End Developer",
    period: "May 2026 - Sep 2026",
    icon: <TrendingUp />,
    color: "from-blue-500 to-cyan-500",

    responsibilities: [
      "Managed the company website and developed responsive pages for integrated facility management services",
      "Built and optimized the Integrated Facility Management landing page and supporting service content",
      "Worked on SEO including on-page SEO, technical SEO, internal linking, canonical URLs, sitemap and robots.txt",
      "Monitored Google Search Console indexing, canonical issues, sitemap coverage and search visibility",
      "Worked on Google Business Profile setup, categories, services and local search visibility",
      "Managed Google Ads setup and conversion tracking for website and call leads",
      "Created social media and hiring content for facility management services, including picker packer and security recruitment posts",
      "Created website content targeting integrated facility management and related services in Bangalore",
      "Worked with GitHub and Vercel for website deployment, updates and production fixes",
      "Improved lead generation through website forms, contact details, calls-to-action and service landing pages",
    ],

    achievements: [
      "Managed the website and digital marketing activities for Acuity Groups LLP",
      "Worked on SEO, Google Business Profile and Google Ads to improve online visibility and enquiries",
      "Handled website deployment and updates through GitHub and Vercel",
    ],

    technologies: [
      "React",
      "Tailwind CSS",
      "SEO",
      "Google Search Console",
      "Google Analytics",
      "Google Ads",
      "GitHub",
      "Vercel",
    ],
  },

  {
    id: 2,
    company: "Urbannest Properties",
    location: "Bengaluru, Karnataka",
    position: "Front-End Developer & Digital Marketing",
    period: "Feb 2025 - Jan 2026",
    icon: <TrendingUp />,
    color: "from-purple-500 to-pink-500",

    responsibilities: [
      "Developed and implemented SEO strategies for BMRDA-approved plots, gated communities and farmland near Bangalore",
      "Managed website content updates for project information, blog posts and property listings",
      "Created social media campaigns to improve engagement, brand awareness and lead enquiries",
      "Monitored Google Analytics and SERP rankings to improve website and marketing performance",
      "Designed and maintained responsive web pages, blog content and landing pages",
      "Worked on website enhancements, UI improvements and regular content updates",
      "Executed email marketing campaigns and promotional offers to nurture leads",
      "Created and optimized website content and blogs to improve user engagement and domain authority",
      "Implemented local SEO for real estate projects and improved visibility in Google Maps and local searches",
      "Coordinated with clients and teams to deliver website and SEO requirements",
    ],

    achievements: [
      "Improved organic search visibility for real estate keywords",
      "Worked on local SEO for real estate projects and Google Maps visibility",
      "Supported website traffic and lead generation through SEO and digital marketing",
    ],

    technologies: [
      "React",
      "Tailwind CSS",
      "SEO",
      "Google Analytics",
      "Google Search Console",
      "Canva",
      "WordPress",
    ],
  },

  {
    id: 3,
    company: "1990 Minds",
    location: "Bengaluru, Karnataka",
    position: "Front-End Developer",
    period: "Feb 2022 - Feb 2024",
    icon: <Code />,
    color: "from-green-500 to-emerald-500",

    responsibilities: [
      "Directed and executed SEO strategies and website development projects for different clients",
      "Managed long-term client relationships through website support and regular project updates",
      "Analyzed client requirements and developed customized web solutions",
      "Built responsive user interfaces using ReactJS and Tailwind CSS",
      "Developed and implemented digital marketing campaigns to improve brand visibility",
      "Managed website updates and maintenance with focus on responsive design and user experience",
      "Executed on-page, off-page and technical SEO strategies and monitored SERP performance",
    ],

    achievements: [
      "Successfully managed multiple client website projects",
      "Delivered responsive websites with improved user experience",
      "Worked across website development, SEO and digital marketing",
    ],

    technologies: [
      "React.js",
      "JavaScript",
      "HTML/CSS",
      "Tailwind CSS",
      "WordPress",
      "SEO",
      "Git",
    ],
  },

  {
    id: 4,
    company: "Vedant Pvt Ltd",
    location: "Bagalkot, Karnataka",
    position: "Networking Engineer",
    period: "2018 - 2022",
    icon: <Building />,
    color: "from-orange-500 to-red-500",

    responsibilities: [
      "Led multiple projects at the University of Horticulture",
      "Handled WiFi installation and camera installation projects",
      "Worked on network infrastructure and project implementation",
      "Coordinated network-related activities and ensured timely completion of projects",
    ],

    achievements: [
      "Worked on networking projects for the University of Horticulture",
      "Handled WiFi and camera installation projects",
      "Supported network infrastructure implementation",
    ],

    technologies: [
      "Networking",
      "WiFi",
      "IP Cameras",
      "Network Infrastructure",
    ],
  },

  {
    id: 5,
    company: "Securetel Networks",
    location: "Bengaluru, Karnataka",
    position: "Networking Engineer",
    period: "2015 - 2018",
    icon: <Briefcase />,
    color: "from-yellow-500 to-orange-500",

    responsibilities: [
      "Managed IP data networks including LAN switching and IP addressing",
      "Worked with IPv4 and IPv6 addressing and network troubleshooting",
      "Configured Ubiquiti Rocket and AirGrid devices for network coverage",
      "Configured EnGenius EAP, ENH and ENS devices",
      "Worked on network security and device configuration",
      "Troubleshot network issues to maintain reliable network performance",
    ],

    achievements: [
      "Worked with LAN switching and IP networking",
      "Configured Ubiquiti and EnGenius network equipment",
      "Worked on network security and troubleshooting",
    ],

    technologies: [
      "CCNP",
      "CCNA",
      "LAN/WAN",
      "IPv4/IPv6",
      "Ubiquiti",
      "EnGenius",
      "Network Security",
    ],
  },
];

const ExperienceCard = ({ experience, isOpen, onClick, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`relative rounded-xl sm:rounded-2xl overflow-hidden group cursor-pointer ${
        isOpen ? "ring-1 sm:ring-2 ring-primary-500" : ""
      }`}
      onClick={onClick}
    >
      {/* Gradient Border */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${experience.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}
      ></div>

      {/* Glass Effect Card */}
      <div className="relative glass-effect p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div className="flex items-start space-x-3 sm:space-x-4">
            <div
              className={`p-2 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-br ${experience.color} shadow-lg flex-shrink-0`}
            >
              {React.cloneElement(experience.icon, {
                className: "w-4 h-4 sm:w-5 sm:h-5",
              })}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white line-clamp-2">
                {experience.position}
              </h3>

              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-1 sm:mt-2">
                <div className="flex items-center space-x-1 text-gray-300 text-xs sm:text-sm">
                  <Building className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="truncate">{experience.company}</span>
                </div>

                <div className="flex items-center space-x-1 text-gray-300 text-xs sm:text-sm mt-1 sm:mt-0">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="truncate">{experience.location}</span>
                </div>

                <div className="flex items-center space-x-1 text-gray-300 text-xs sm:text-sm mt-1 sm:mt-0">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{experience.period}</span>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            className="text-gray-400 flex-shrink-0 ml-2"
          >
            {isOpen ? (
              <ChevronUp className="w-4 h-4 sm:w-6 sm:h-6" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-6 sm:h-6" />
            )}
          </motion.div>
        </div>

        {/* Technologies */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {experience.technologies.map((tech, i) => (
            <span
              key={i}
              className="px-2 py-0.5 sm:px-3 sm:py-1 text-xs rounded-full bg-white/10 text-gray-300 whitespace-nowrap"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* Expanded Content */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-3 sm:pt-4 border-t border-white/10">
                {/* Responsibilities */}
                <div className="mb-4 sm:mb-6">
                  <h4 className="font-semibold text-sm sm:text-lg mb-2 sm:mb-3 flex items-center space-x-2">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                    <span>Key Responsibilities</span>
                  </h4>

                  <ul className="space-y-1.5 sm:space-y-2">
                    {experience.responsibilities.map((resp, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-start space-x-2 text-gray-300 text-sm sm:text-base"
                      >
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-primary-500 mt-1.5 sm:mt-2 flex-shrink-0"></div>

                        <span>{resp}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Achievements */}
                <div>
                  <h4 className="font-semibold text-sm sm:text-lg mb-2 sm:mb-3">
                    Achievements
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                    {experience.achievements.map((achievement, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 }}
                        className="p-2 sm:p-3 rounded-lg bg-white/5 border border-white/10"
                      >
                        <div className="text-xs sm:text-sm text-gray-300">
                          {achievement}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Timeline Line - Mobile Only */}
      {index < experiences.length - 1 && (
        <div className="block sm:hidden absolute left-4 right-4 bottom-0 h-0.5 bg-gradient-to-r from-primary-500/20 to-transparent"></div>
      )}
    </motion.div>
  );
};

const Experience = () => {
  const [openCard, setOpenCard] = useState(0);

  return (
    <section
      id="experience"
      className="py-12 sm:py-16 md:py-20 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute top-1/4 right-4 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-primary-500/5 rounded-full blur-xl sm:blur-2xl md:blur-3xl"></div>

      <div className="absolute bottom-1/4 left-4 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-secondary-500/5 rounded-full blur-xl sm:blur-2xl md:blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 max-w-6xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full glass-effect border border-white/10 mb-3 sm:mb-4">
            <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
            <span className="text-xs sm:text-sm">Career Journey</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 md:mb-6">
            Professional
            <span className="block gradient-text text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
              Experience
            </span>
          </h2>

          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto px-2">
            From networking engineer to front-end developer and digital
            marketing professional
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Central Timeline Line */}
          <div className="hidden sm:block absolute left-4 lg:left-1/2 transform lg:-translate-x-1/2 top-0 bottom-0 w-0.5 sm:w-1 bg-gradient-to-b from-primary-500 via-secondary-500 to-transparent"></div>

          {/* Experience Cards */}
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            {experiences.map((exp, index) => (
              <div
                key={exp.id}
                className={`relative sm:pl-8 lg:pl-0 ${
                  index % 2 === 0
                    ? "lg:pr-1/2 lg:pl-8"
                    : "lg:pl-1/2 lg:pr-8"
                }`}
              >
                {/* Timeline Dot */}
                <div
                  className={`absolute top-5 sm:top-6 left-0 sm:left-3 lg:left-1/2 transform lg:-translate-x-1/2`}
                >
                  <div className="relative">
                    <div
                      className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-gradient-to-r ${exp.color}`}
                    ></div>

                    <div
                      className={`absolute inset-0 w-3 h-3 sm:w-4 sm:h-4 rounded-full animate-ping bg-gradient-to-r ${exp.color} opacity-20`}
                    ></div>
                  </div>
                </div>

                {/* Mobile Date */}
                <div className="block sm:hidden text-xs text-gray-400 mb-2 ml-4">
                  {exp.period}
                </div>

                <ExperienceCard
                  experience={exp}
                  isOpen={openCard === index}
                  onClick={() =>
                    setOpenCard(openCard === index ? null : index)
                  }
                  index={index}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          className="mt-8 sm:mt-12 md:mt-20 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-6"
        >
          {[
            { value: "9+", label: "Years Experience" },
            { value: "Google", label: "Certified" },
            { value: "20+", label: "Projects" },
            { value: "100%", label: "Satisfaction" },
          ].map((stat, index) => (
            <div
              key={index}
              className="p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl glass-effect border border-white/10 text-center group hover:border-primary-500/50 transition-all"
            >
              <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold gradient-text group-hover:scale-105 transition-transform">
                {stat.value}
              </div>

              <div className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-2">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;